import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const EULA = () => {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-logo">🎙️ EchoMemoir</Link>
      </header>

      <main className="legal-main">
        <h1>End User License Agreement</h1>
        <p className="legal-updated">Last updated: June 20, 2026</p>

        <section>
          <p>
            PLEASE READ THIS END USER LICENSE AGREEMENT ("EULA" OR "AGREEMENT") CAREFULLY BEFORE USING THE
            ECHOMEMOIR SERVICE. BY CREATING AN ACCOUNT OR USING ECHOMEMOIR, YOU AGREE TO BE BOUND BY THESE
            TERMS. IF YOU DO NOT AGREE, DO NOT USE THE SERVICE.
          </p>
          <p>
            This Agreement is between you ("User," "you," or "your") and Technolica, Inc., a Delaware corporation
            ("Technolica," "we," "us," or "our"), the developer of EchoMemoir (echomemoir.ai).
          </p>
        </section>

        <section>
          <h2>1. Grant of License</h2>
          <p>
            Subject to your compliance with this Agreement and payment of any applicable subscription fees,
            Technolica grants you a limited, non-exclusive, non-transferable, revocable license to access and
            use the EchoMemoir Service for your personal, non-commercial family memory preservation purposes.
          </p>
        </section>

        <section>
          <h2>2. Accounts and Eligibility</h2>
          <p>
            You must be at least 13 years of age to create an account. By creating an account, you represent
            that you are at least 13 years old and that all information you provide is accurate and current.
            You are responsible for maintaining the confidentiality of your login credentials and for all
            activity under your account. Notify us immediately at{' '}
            <a href="mailto:support@echomemoir.ai">support@echomemoir.ai</a> if you believe your account has
            been compromised.
          </p>
        </section>

        <section>
          <h2>3. User Content</h2>

          <h3>3.1 Your Content</h3>
          <p>
            EchoMemoir allows you to upload, create, and share content including text, photos, audio recordings,
            and video recordings ("User Content"). You retain all ownership rights in your User Content.
          </p>

          <h3>3.2 License to Technolica</h3>
          <p>
            By uploading or submitting User Content to EchoMemoir, you grant Technolica a limited, non-exclusive,
            royalty-free, worldwide license to store, transcode, reproduce, display, and deliver your User Content
            solely for the purpose of operating and providing the Service to you and your designated circle members.
            This license does not permit us to use your content for advertising, marketing to third parties, or any
            purpose beyond operating the Service on your behalf.
          </p>

          <h3>3.3 Video Content</h3>
          <p>When you upload video files, you acknowledge and agree that:</p>
          <ul>
            <li>Technolica will transcode your video into HLS (HTTP Live Streaming) format to enable efficient playback across devices. This transcoding process does not alter the substantive content of your video.</li>
            <li>Video files and streaming segments will be stored on Amazon Web Services (AWS) S3 and delivered via AWS CloudFront using time-limited signed URLs.</li>
            <li>Transcoding is performed automatically upon upload. Processing times may vary based on file size, format, and server load.</li>
            <li>You are solely responsible for ensuring you have the right to upload any video content, including obtaining any necessary consent from individuals appearing in videos.</li>
            <li>Video uploads are subject to size and format limitations as posted on the Service; Technolica reserves the right to adjust these limits.</li>
            <li>Technolica does not review video content for rights clearance. If you upload videos containing third-party copyrighted material (music, film clips, broadcasts, etc.), you do so at your own risk.</li>
          </ul>

          <h3>3.4 Content Standards</h3>
          <p>You agree not to upload User Content that:</p>
          <ul>
            <li>Violates any applicable law or regulation</li>
            <li>Infringes the intellectual property rights of any third party, including copyrighted music, video clips, or other protected works embedded in your videos</li>
            <li>Contains pornographic, sexually explicit, or obscene material</li>
            <li>Depicts or promotes violence, abuse, or illegal activity</li>
            <li>Includes the personal information of minors in a manner that could endanger those individuals</li>
            <li>Is defamatory, harassing, or threatening toward any individual</li>
          </ul>
          <p>
            Technolica reserves the right to remove content that violates these standards and to suspend or
            terminate accounts of repeat violators, without prior notice.
          </p>

          <h3>3.5 DMCA / Copyright Infringement</h3>
          <p>
            If you believe User Content on EchoMemoir infringes your copyright, you may submit a notice to our
            designated agent at <a href="mailto:privacy@echomemoir.ai">privacy@echomemoir.ai</a> with the
            information required by the Digital Millennium Copyright Act (DMCA). We will respond to valid notices
            in accordance with applicable law.
          </p>
        </section>

        <section>
          <h2>4. Subscriptions, Payments, and Gifts</h2>
          <p>
            Access to certain features requires a paid subscription. Subscription fees are charged in advance on
            a recurring basis (monthly or annually, as selected). All fees are non-refundable except as required
            by law or as expressly stated in our refund policy. We reserve the right to change subscription fees
            upon reasonable notice. Gift subscriptions are non-refundable once redeemed.
          </p>
          <p>
            Payments are processed by Stripe. By providing payment information, you agree to Stripe's terms of
            service. We do not store your full payment card number.
          </p>
        </section>

        <section>
          <h2>5. Free Tier Limitations</h2>
          <p>
            Free tier accounts are subject to limits on the number of stories, circle members, and storage
            (including video storage) as defined on our pricing page, which may be updated from time to time.
            Technolica reserves the right to modify free tier limits with reasonable notice.
          </p>
        </section>

        <section>
          <h2>6. Intellectual Property</h2>
          <p>
            All rights, title, and interest in the EchoMemoir platform, including software, design, trademarks,
            and content created by Technolica, remain the exclusive property of Technolica, Inc. This Agreement
            does not grant you any rights in Technolica's intellectual property beyond the limited license
            described in Section 1. "EchoMemoir" and related logos are trademarks of Technolica, Inc.
          </p>
        </section>

        <section>
          <h2>7. Privacy</h2>
          <p>
            Your use of EchoMemoir is subject to our <Link to="/privacy">Privacy Policy</Link>. Our Privacy
            Policy describes how we collect, use, and protect your personal information, including video content
            and associated metadata. By using the Service, you consent to our data practices as described in the
            Privacy Policy.
          </p>
        </section>

        <section>
          <h2>8. Third-Party Services</h2>
          <p>
            EchoMemoir integrates with third-party services including AWS (storage and delivery), Stripe
            (payments), SendGrid (email), and Auth0 (authentication). Your use of these integrations may be
            subject to the terms of those third parties. Technolica is not responsible for the practices of
            third-party services.
          </p>
        </section>

        <section>
          <h2>9. Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
            IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, AND NON-INFRINGEMENT. TECHNOLICA DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
            ERROR-FREE, OR THAT VIDEO TRANSCODING OR DELIVERY WILL BE AVAILABLE AT ALL TIMES.
          </p>
          <p>
            We do not guarantee the permanent preservation of your content. You are strongly encouraged to
            maintain your own backups of important videos, photos, and other media.
          </p>
        </section>

        <section>
          <h2>10. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, TECHNOLICA SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF DATA, LOSS OF CONTENT
            (INCLUDING VIDEO CONTENT), OR LOSS OF PROFITS, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            TECHNOLICA'S TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING UNDER THIS AGREEMENT SHALL NOT EXCEED THE
            AMOUNT YOU PAID TO TECHNOLICA IN THE TWELVE MONTHS PRECEDING THE CLAIM, OR $50, WHICHEVER IS GREATER.
          </p>
        </section>

        <section>
          <h2>11. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Technolica and its officers, directors, employees,
            and agents from any claims, damages, losses, and expenses (including reasonable attorneys' fees)
            arising out of: (a) your use of the Service; (b) your User Content, including any video content you
            upload; (c) your violation of this Agreement; or (d) your infringement of any third-party rights,
            including intellectual property or privacy rights.
          </p>
        </section>

        <section>
          <h2>12. Termination</h2>
          <p>
            Either party may terminate this Agreement at any time. You may delete your account through the
            Settings page. Technolica may suspend or terminate your account for violation of this Agreement,
            non-payment, or any other reason at our discretion with or without notice. Upon termination, your
            license to use the Service ends and Technolica may delete your content in accordance with our data
            retention policies. Sections 3.2, 6, 9, 10, 11, and 14 survive termination.
          </p>
        </section>

        <section>
          <h2>13. Modifications to the Agreement</h2>
          <p>
            Technolica may update this Agreement from time to time. We will notify you of material changes via
            email or in-app notice. Continued use of the Service after the effective date of changes constitutes
            your acceptance. If you do not agree to an updated Agreement, you must stop using the Service and
            may delete your account.
          </p>
        </section>

        <section>
          <h2>14. Governing Law and Disputes</h2>
          <p>
            This Agreement is governed by the laws of the State of California, without regard to conflict of law
            principles. Any disputes arising under this Agreement shall be resolved by binding arbitration
            administered by JAMS in Alameda County, California, except that either party may seek injunctive or
            other equitable relief in a court of competent jurisdiction. You waive any right to participate in a
            class action lawsuit or class-wide arbitration.
          </p>
        </section>

        <section>
          <h2>15. Miscellaneous</h2>
          <p>
            This Agreement, together with our <Link to="/privacy">Privacy Policy</Link> and{' '}
            <Link to="/terms">Terms of Service</Link>, constitutes the entire agreement between you and
            Technolica regarding EchoMemoir. If any provision is found unenforceable, the remaining provisions
            remain in full force. Our failure to enforce any right or provision does not constitute a waiver.
            You may not assign your rights under this Agreement without our written consent. Technolica may
            assign its rights and obligations freely.
          </p>
        </section>

        <section>
          <h2>16. Contact</h2>
          <p>
            For questions about this Agreement, contact us at{' '}
            <a href="mailto:support@echomemoir.ai">support@echomemoir.ai</a> or visit{' '}
            <a href="https://echomemoir.ai">echomemoir.ai</a>.
          </p>
        </section>
      </main>

      <footer className="legal-footer">
        <Link to="/privacy">Privacy Policy</Link>
        <span>·</span>
        <Link to="/terms">Terms of Service</Link>
        <span>·</span>
        <Link to="/eula">EULA</Link>
        <span>·</span>
        <span>© {new Date().getFullYear()} EchoMemoir</span>
      </footer>
    </div>
  );
};

export default EULA;
